import { compact } from 'lodash';
import { combineEpics, ofType } from 'redux-observable';
import { EMPTY, from, Observable, of } from 'rxjs';
import { catchError, debounceTime, map, mergeMap, switchMap } from 'rxjs/operators';

import { Action } from '../actions';
import {
    changeVCardStatus,
    FETCH_PHONE_CODE_BY_ADDRESS_FOR_VCARD,
    fetchPhoneCodeByAddressForVCard,
    FetchPhoneCodeByAddressForVCard,
    fetchVCardMetroStation,
    ON_CHANGE_VCARD_ADDRESS_MAP_POINT,
    ON_SET_WARNING_TOOLTIP_VCARD_ADDRESS_FORM,
    ON_UPDATE_VCARD_ADDRESS_FORM_BY_FIELD,
    OnChangeVCardAddressMapPoint,
    OnSetWarningTooltipVCardAddressForm,
    OnUpdateVCardAddressFormByField,
    onUpdateVCardAddressFormByField,
    OPEN_VCARD_EDITOR,
    OpenVCardEditor,
    showWarningTooltipVCardAddressForm,
    UPDATE_VCARD_ADDRESS_INPUT_SUGGEST,
    updatePhoneCodeByRegionIdForVCard,
    updateSelfChooseVCardMapPosition,
    UpdateVCardAddressInputSuggestAction,
    updateVCardAddressMapPoint,
    updateVCardAddressSuggest,
    updateVCardGeoObject
} from '../actions/visitingCardEditor';
import {
    AddressField,
    getAddressFieldByAddressFieldHashForGeoRequest,
    getAddressString,
    getFullAddressText,
    getShiftBoundsByCurrentLevel
} from '../service/AddressHelper/AddressHelper';
import { isCityWithMetro } from '../service/BannerHelpers/BannerHelpers';
import {
    MAP_GEO_SUGGEST_VERSION,
    MapApiSuggestRequestSearchType,
    SuggestItem,
    YandexMapHelper
} from '../service/YandexMapHelper/YandexMapHelper';
import { VCardPhoneFields, VcardStatus } from '../typings/banners';
import { DnaEpic } from '../typings/epic';

const DEBOUNCE_TIME = 200;
const VCARD_ADDRESS_FIELDS_SUGGEST_ITEMS_COUNT = 1;

const onVCardAddressChangedEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(UPDATE_VCARD_ADDRESS_INPUT_SUGGEST),
        debounceTime(DEBOUNCE_TIME),
        mergeMap(
            (action: UpdateVCardAddressInputSuggestAction): Observable<Action> => {
                const vCardData = store$.value.visitingCardEditor[action.editorId].vCardData;

                if (!vCardData) {
                    return EMPTY;
                }

                const fullAddress = getAddressString(vCardData.address, action.field);

                return from(
                    YandexMapHelper.getMapApiSuggestItems({
                        part: fullAddress,
                        search_type: MapApiSuggestRequestSearchType.addr,
                        v: MAP_GEO_SUGGEST_VERSION,
                        n: VCARD_ADDRESS_FIELDS_SUGGEST_ITEMS_COUNT,
                        bases: getAddressFieldByAddressFieldHashForGeoRequest(action.field).join(',')
                    })
                ).pipe(
                    map(
                        (listSuggest: SuggestItem[]): Action => {
                            const argSugget: string[] = listSuggest.map((itemSuggest: SuggestItem) => {
                                return itemSuggest.title.text;
                            });

                            return updateVCardAddressSuggest(argSugget, action.editorId, action.field);
                        }
                    ),
                    catchError(
                        (): Observable<Action> => of(updateVCardAddressSuggest([], action.editorId, action.field))
                    )
                );
            }
        )
    );
};

const onVCardUpdateAddressFormEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(ON_UPDATE_VCARD_ADDRESS_FORM_BY_FIELD),
        debounceTime(DEBOUNCE_TIME),
        mergeMap(
            (action: OnUpdateVCardAddressFormByField): Observable<Action> => {
                const { vCardData } = store$.value.visitingCardEditor[action.editorId];

                if (!vCardData) {
                    return EMPTY;
                }

                const fullAddress = getFullAddressText(vCardData.address);

                return from(YandexMapHelper.getGeoObject(fullAddress)).pipe(
                    switchMap(({ geoObject, geoId }): Array<Action> | Observable<Action> => {
                        const bounds = YandexMapHelper.getZoomBounds(
                            geoObject.geometry.getBounds(),
                            getShiftBoundsByCurrentLevel(AddressField.house)
                        );
                        const coords = geoObject.geometry.getCoordinates();

                        // так исторически сложилось, что в директе всё хранится вот в таком виде - это отправляется в БК
                        const pointInfo = YandexMapHelper.getPointInfo(coords, bounds);

                        const actions: Array<Action> = [changeVCardStatus(action.editorId, VcardStatus.EDITING)];

                        if (geoObject) {
                            if (action.field === AddressField.country && vCardData.address.country) {
                                actions.push(
                                    fetchPhoneCodeByAddressForVCard(
                                        vCardData.address.country,
                                        action.editorId,
                                        VCardPhoneFields.codeCountry
                                    )
                                );
                            }

                            if (
                                action.field === AddressField.city &&
                                vCardData.address.country &&
                                vCardData.address.city
                            ) {
                                actions.push(
                                    fetchPhoneCodeByAddressForVCard(
                                        [vCardData.address.country, vCardData.address.city].join(', '),
                                        action.editorId,
                                        VCardPhoneFields.codeRegion
                                    )
                                );
                            }

                            actions.push(updateVCardAddressMapPoint(pointInfo, action.editorId, geoId, geoObject));

                            if (isCityWithMetro(geoId) && vCardData.address.city) {
                                const [x, y] = geoObject.geometry.getCoordinates();

                                actions.push(fetchVCardMetroStation(action.editorId, y, x));
                            }
                        }

                        return actions;
                    }),
                    catchError(() => of(changeVCardStatus(action.editorId, VcardStatus.EDITING)))
                );
            }
        )
    );
};

const onAcceptVCardWarningTooltipEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(ON_SET_WARNING_TOOLTIP_VCARD_ADDRESS_FORM),
        mergeMap(
            (action: OnSetWarningTooltipVCardAddressForm): Observable<Action> => {
                const { vCardData } = store$.value.visitingCardEditor[action.editorId];

                if (!vCardData || !isCityWithMetro(vCardData.geoId)) {
                    return EMPTY;
                }

                const [x, y] = vCardData.geoObject.geometry.getCoordinates();

                return of(fetchVCardMetroStation(action.editorId, y, x));
            }
        )
    );
};

const onVCardUpdateAddressMapPointEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(ON_CHANGE_VCARD_ADDRESS_MAP_POINT),
        mergeMap(
            (action: OnChangeVCardAddressMapPoint): Observable<Action> => {
                const { pointOnMap, editorId } = action;
                const { vCardData } = store$.value.visitingCardEditor[editorId];

                if (!vCardData || !pointOnMap) {
                    return EMPTY;
                }

                return from(YandexMapHelper.getGeoObject([pointOnMap.x, pointOnMap.y])).pipe(
                    mergeMap(({ geoObject, geoId }): Array<Action> | Observable<Action> => {
                        if (geoObject) {
                            const oldGeoObject = vCardData.geoObject;

                            if (oldGeoObject) {
                                return [
                                    showWarningTooltipVCardAddressForm(
                                        editorId,
                                        pointOnMap,
                                        geoId,
                                        YandexMapHelper.createGeoObjectFromPoint([pointOnMap.x, pointOnMap.y]),
                                        geoObject
                                    )
                                ];
                            } else {
                                return [
                                    updateVCardGeoObject(action.editorId, geoId, geoObject),
                                    updateVCardAddressMapPoint(pointOnMap, action.editorId, geoId, geoObject)
                                ];
                            }
                        }

                        return EMPTY;
                    }),
                    catchError(() => EMPTY)
                );
            }
        )
    );
};

const onOpenVCardEditorEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(OPEN_VCARD_EDITOR),
        mergeMap(
            (action: OpenVCardEditor): Observable<Action> => {
                const { editorId } = action;
                const { vCardData } = store$.value.visitingCardEditor[editorId];

                return from(YandexMapHelper.initApiMap()).pipe(
                    mergeMap((): Array<Action> | Observable<Action> => {
                        if (!vCardData || !vCardData.address) {
                            return EMPTY;
                        }

                        const { pointOnMap } = vCardData;

                        return compact([
                            onUpdateVCardAddressFormByField(editorId, AddressField.country),
                            pointOnMap &&
                                updateSelfChooseVCardMapPosition(
                                    editorId,
                                    YandexMapHelper.createGeoObjectFromPoint([pointOnMap.x, pointOnMap.y])
                                )
                        ]);
                    }),
                    catchError(() => EMPTY)
                );
            }
        )
    );
};

const fetchPhoneCodeByRegionIdForVCardEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(FETCH_PHONE_CODE_BY_ADDRESS_FOR_VCARD),
        mergeMap(
            (action: FetchPhoneCodeByAddressForVCard): Observable<Action> => {
                return from(YandexMapHelper.getGeoObject(action.address)).pipe(
                    mergeMap(
                        ({ geoId }): Observable<Action> => {
                            if (geoId) {
                                return from(deps.commonDataProvider.fetchPhoneCodeByRegionId(geoId)).pipe(
                                    mergeMap(
                                        (data): Observable<Action> => {
                                            const code = data.data.getPhoneCodeByRegionId.codes[0];

                                            if (code) {
                                                return of(
                                                    updatePhoneCodeByRegionIdForVCard(
                                                        code,
                                                        action.editorId,
                                                        action.codeType
                                                    )
                                                );
                                            } else {
                                                return EMPTY;
                                            }
                                        }
                                    )
                                );
                            } else {
                                return EMPTY;
                            }
                        }
                    ),
                    catchError((): Observable<Action> => EMPTY)
                );
            }
        )
    );
};

export const visitingCardEditorEpic = combineEpics(
    onVCardAddressChangedEpic,
    onVCardUpdateAddressFormEpic,
    onVCardUpdateAddressMapPointEpic,
    onOpenVCardEditorEpic,
    fetchPhoneCodeByRegionIdForVCardEpic,
    onAcceptVCardWarningTooltipEpic
);
